import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Typography,
} from '@mui/material'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactElement, useState } from 'react'
import { ShowNotesPreviewModal as EditorPreviewModal } from 'src/v3/components/show-notes-editor/components/show-notes-preview-modal'
import { useShowNotesBlocksRendered } from 'src/v3/components/show-notes-editor/hooks'
import { PIPELINE_SHOW_NOTES_BASE_PATH } from '../constants'

type Props = {
  isOpen: boolean
  onClose: () => void
  showNotesID: string
  showNotesName: string
  showNotesDescription?: string | null
}

function CourseShowNotesPreviewModalInner({
  isOpen,
  onClose,
  showNotesID,
  showNotesName,
  showNotesDescription,
}: Props): ReactElement {
  let {
    data: blocks,
    isLoading,
    isError,
    refetch,
  } = useShowNotesBlocksRendered(
    showNotesID,
    PIPELINE_SHOW_NOTES_BASE_PATH,
    isOpen,
  )

  if (isLoading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (isError) {
    return (
      <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="xs">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6">Failed to load preview</Typography>
          <Typography color="text.secondary" variant="body2">
            Could not load the content for <strong>{showNotesName}</strong>.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={() => refetch()}>
              Retry
            </Button>
            <Button onClick={onClose}>Close</Button>
          </Box>
        </Box>
      </Dialog>
    )
  }

  return (
    <EditorPreviewModal
      open={isOpen}
      onClose={onClose}
      blocks={blocks ?? []}
      basePath={PIPELINE_SHOW_NOTES_BASE_PATH}
      showNotesName={showNotesName}
      showNotesDescription={showNotesDescription ?? undefined}
    />
  )
}

export default function CourseShowNotesPreviewModal(
  props: Props,
): ReactElement {
  let [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 0,
            gcTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      }),
  )

  return (
    <QueryClientProvider client={queryClient}>
      <CourseShowNotesPreviewModalInner {...props} />
    </QueryClientProvider>
  )
}
